import { useMemo, useState } from 'react';
import { MessageSquareDashed, SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAgentContext } from '@Providers';

import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@Components/ui-v2/empty';
import { useAgentConversations } from '@CustomHooks';
import useAgentRuntime from '../../../CustomHooks/Agents/useAgentRuntime';
import useChatManagement from '../../../CustomHooks/Agents/useChatManagement';
import { GROUP_I18N_KEY, groupConversations } from './groupConversations';
import NavSidebarItem from './NavSidebarItem';
import NavSidebarSearch from './NavSidebarSearch';
import NavSidebarSkeleton from './NavSidebarSkeleton';

interface ConversationListBodyProps {
  /** Notifies the parent so a popover host can close itself on selection. */
  onAfterSwitch?: () => void;
  /** Adjusts vertical density for cramped dropdown surfaces. */
  compact?: boolean;
}

// Shared by NavSidebar (fullscreen) and SessionDropdown (compact); owns search, delegates actions to useChatManagement.
const ConversationListBody = ({ onAfterSwitch, compact = false }: ConversationListBodyProps) => {
  const { t } = useTranslation();
  const { activeChatId, selectedAgent } = useAgentContext();
  const { conversations, isLoading } = useAgentConversations(selectedAgent?.source_name);
  const { switchChat, updateConversationTitle, deleteConversation } = useChatManagement();
  const { loadingIds, unreadIds } = useAgentRuntime();
  const [search, setSearch] = useState('');

  const filteredConversations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter((c) => (c.title || '').toLowerCase().includes(term));
  }, [conversations, search]);

  const groups = useMemo(() => groupConversations(filteredConversations), [filteredConversations]);

  const handleSwitch = (conversationId: string) => {
    switchChat(conversationId);
    onAfterSwitch?.();
  };

  const handleRename = (conversationId: string, title: string) => {
    updateConversationTitle.mutate({ conversationId, title });
  };

  const handleDelete = (conversationId: string) => {
    deleteConversation.mutate(conversationId);
  };

  const verticalPad = compact ? 'pt-2 pb-2' : 'pt-3 pb-3';
  const groupSpacing = compact ? 'space-y-3' : 'space-y-4';

  return (
    <>
      {!compact && <NavSidebarSearch value={search} onChange={setSearch} />}

      <div className="flex-1 overflow-y-auto">
        {isLoading && conversations.length === 0 ? (
          <NavSidebarSkeleton />
        ) : groups.length === 0 ? (
          <Empty className="h-full gap-4 border-0 p-4">
            <EmptyMedia variant="icon">
              {search ? <SearchX strokeWidth={1.75} /> : <MessageSquareDashed strokeWidth={1.75} />}
            </EmptyMedia>
            <EmptyHeader>
              <EmptyTitle className="text-sm">
                {search ? t('Agent.EmptySearchTitle') : t('Agent.EmptyConversationsTitle')}
              </EmptyTitle>
              <EmptyDescription className="text-xs">
                {search
                  ? t('Agent.EmptySearchDescription', { term: search })
                  : t('Agent.EmptyConversationsDescription')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className={`${groupSpacing} px-3 ${verticalPad}`}>
            {groups.map((group) => (
              <div key={group.key} className="space-y-1">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {t(GROUP_I18N_KEY[group.key])}
                </p>
                {group.items.map((conversation) => (
                  <NavSidebarItem
                    key={conversation.conversation_id}
                    conversation={conversation}
                    isActive={conversation.conversation_id === activeChatId}
                    isLoading={loadingIds.has(conversation.conversation_id)}
                    isUnread={unreadIds.has(conversation.conversation_id)}
                    onClick={handleSwitch}
                    onRename={handleRename}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ConversationListBody;
